/**
 * Cartesian chart families: line, area, bar, stackedBar, horizontalBar, scatter.
 *
 * A series whose declared field is absent from the data is skipped with a
 * development warning instead of plotting NaN. That silent-coercion failure is
 * exactly what the downstream `extractValue`/`extractLabel` guesswork was
 * papering over.
 */
import type { EChartsOption } from 'echarts';
import { chartTokens, type ChartMedia, type ChartMode } from '@/tokens/chart';
import type { ChartConfig, ChartRow, SeriesConfig } from '../types';
import { baseOption, seriesColor } from './shared';

const STACK_ID = 'total';

const MARK: Record<string, 'line' | 'bar' | 'scatter'> = {
  line: 'line',
  area: 'line',
  bar: 'bar',
  stackedBar: 'bar',
  horizontalBar: 'bar',
  scatter: 'scatter',
};

const toNumber = (value: unknown): number => {
  const n = typeof value === 'number' ? value : Number(value);
  return Number.isFinite(n) ? n : 0;
};

function hasField(data: readonly ChartRow[], field: string | undefined): field is string {
  return typeof field === 'string' && data.length > 0 && field in data[0];
}

export function buildCartesianOption(
  config: ChartConfig,
  data: readonly ChartRow[],
  mode: ChartMode,
  media: ChartMedia,
): EChartsOption {
  const tokens = chartTokens[mode];
  const mark = MARK[config.chartType] ?? 'line';
  const horizontal = config.chartType === 'horizontalBar';
  const stacked = config.chartType === 'stackedBar';

  const visible = config.series.filter((series) => !series.hidden);
  const usable = visible.filter((series) => {
    const ok = hasField(data, series.fields.x) && hasField(data, series.fields.y);
    if (!ok && data.length > 0) {
      console.warn(
        `[octopus-ui/chart] series "${series.name}" references fields ` +
          `x="${series.fields.x}" y="${series.fields.y}" which are not present in the data; ` +
          'skipping this series.',
      );
    }
    return ok;
  });

  const categories = usable.length
    ? data.map((row) => String(row[usable[0].fields.x as string]))
    : [];

  const categoryAxis = {
    type: 'category' as const,
    data: categories,
    axisLine: { lineStyle: { color: tokens.ink.axis } },
    axisTick: { show: false },
    axisLabel: { color: tokens.ink.label },
    splitLine: { show: false },
  };

  const valueAxis = {
    type: 'value' as const,
    axisLine: { show: false },
    axisTick: { show: false },
    axisLabel: { color: tokens.ink.label },
    splitLine: { lineStyle: { color: tokens.ink.grid } },
  };

  const series = usable.map((entry: SeriesConfig, index: number) => {
    const color = seriesColor(index, entry, mode);
    const values = data.map((row) => toNumber(row[entry.fields.y as string]));

    return {
      id: entry.id,
      name: entry.name,
      type: mark,
      itemStyle: {
        color,
        ...(mark === 'bar'
          ? { borderRadius: horizontal ? [0, 4, 4, 0] : [4, 4, 0, 0] }
          : {}),
      },
      ...(mark === 'line'
        ? {
            smooth: true,
            showSymbol: false,
            lineStyle: { color, width: 2 },
            ...(config.chartType === 'area'
              ? { areaStyle: { color, opacity: tokens.ink.areaOpacity } }
              : {}),
          }
        : {}),
      ...(mark === 'bar'
        ? {
            // A visible surface gap between adjacent fills is the mandated
            // secondary encoding for colour-vision separation.
            barCategoryGap: '30%',
            barGap: '8%',
            ...(stacked ? { stack: STACK_ID } : {}),
          }
        : {}),
      data:
        mark === 'scatter'
          ? data.map((row) => [
              row[entry.fields.x as string],
              toNumber(row[entry.fields.y as string]),
            ])
          : values,
    };
  });

  return {
    ...baseOption(config, mode, media),
    xAxis: horizontal ? valueAxis : categoryAxis,
    yAxis: horizontal ? categoryAxis : valueAxis,
    series,
  } as EChartsOption;
}
